import { toNestErrors } from "@hookform/resolvers";
import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { useEffect } from "react";
import type { FieldErrors, FieldValues, Mode, Resolver, UseFormProps } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { z } from "zod/v4";
import { debugLog } from "../../../utils/debug";
import { makeLinguiZodErrorMap } from "./make-lingui-zod-error-map";
import { type ServerError, useServerErrorToFormError } from "./useServerErrorToFormError";

// Zod v4 lets an issue's own `message` win over the error map, so a custom issue raised with both an
// English `message` and a translated `params.i18n` descriptor would show English in every language.
// This resolver mirrors zodResolver, but renders such issues through the Lingui error map instead.
export const linguiZodResolver =
	<TFieldValues extends FieldValues>(schema: z.ZodType<TFieldValues>, i18n: I18n): Resolver<TFieldValues> =>
	async (values, _context, options) => {
		const errorMap = makeLinguiZodErrorMap(i18n);
		const result = await schema.safeParseAsync(values, { error: errorMap });
		if (result.success) {
			return { values: result.data, errors: {} };
		}

		const fieldErrors: FieldErrors = {};
		for (const topIssue of result.error.issues) {
			const issue = topIssue.code === "invalid_union" ? (topIssue.errors[0]?.[0] ?? topIssue) : topIssue;
			const path = topIssue.path.join(".");
			if (fieldErrors[path]) {
				continue;
			}
			const translated =
				issue.code === "custom" && issue.params?.i18n ? errorMap({ ...issue, input: undefined }) : undefined;
			const message = typeof translated === "object" && translated?.message ? translated.message : issue.message;
			fieldErrors[path] = { type: issue.code, message };
		}
		return { values: {}, errors: toNestErrors(fieldErrors, options) };
	};

export type UseEnhancedFormProps<TFieldValues extends FieldValues> = {
	initialValues?: UseFormProps<TFieldValues>["defaultValues"] | any;
	mode?: Mode;
	schema: z.ZodType<TFieldValues>;
	serverError?: ServerError | undefined | null;
	t?: unknown;
};

export const useEnhancedForm = <TFieldValues extends FieldValues>({
	initialValues,
	mode = "onBlur",
	schema,
	serverError,
}: UseEnhancedFormProps<TFieldValues>) => {
	const { i18n } = useLingui();
	const methods = useForm<TFieldValues>({
		defaultValues: initialValues,
		mode,
		resolver: schema ? linguiZodResolver(schema, i18n) : undefined,
	});

	useServerErrorToFormError({
		serverError,
		setError: methods.setError as any,
	});

	const { errors, isSubmitting, isValid, isDirty } = methods.formState;

	useEffect(() => {
		debugLog("formState", { errors, isSubmitting, isValid, isDirty });
	}, [errors, isSubmitting, isValid, isDirty]);

	return methods;
};
